/**
 * Lykke on every page but Home: a button at the bottom right and the conversation above it. On a campaign page it is
 * that campaign's conversation (with what the campaign needs from you on top); elsewhere, the home conversation.
 * `?lykke=1` opens it (links from e-mails and from "Needs you").
 *
 * `CampaignLykkePanel` is the same campaign conversation as a block, for the campaign page's "Ask Lykke" slot.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { HOME, useLykke } from './LykkeContext';
import { LykkeConversation } from './LykkeConversation';
import { LykkeAvatar } from './LykkeAvatar';
import { actionable, NeedsYouList, useNeedsYou } from './NeedsYou';

/** One campaign's needs and conversation, as a block (the campaign page's "Ask Lykke" slot uses it as is). */
export function CampaignLykkePanel({ campaignId, className }: { campaignId: string; className?: string }) {
  const l = useLykke();
  const needs = useNeedsYou(!!l?.available, campaignId);
  const [showNeeds, setShowNeeds] = useState(false);
  if (!l?.available) return null;
  const open = actionable(needs.data);
  return (
    <div className={cn('flex min-h-0 flex-col', className)} data-lykke-campaign={campaignId}>
      {open.length > 0 && (
        <div className="border-b border-[#f2f4f7] px-4 py-2.5">
          <button type="button" onClick={() => setShowNeeds((v) => !v)} aria-expanded={showNeeds} className="flex w-full items-center gap-2 text-left text-[12.5px] font-semibold text-[#101828]">
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[#be43ff] px-1.5 text-[11px] font-bold text-white">{open.length}</span>
            {open.length === 1 ? 'thing needs you here' : 'things need you here'}
            <ChevronDown className={cn('ml-auto h-4 w-4 text-[#667085] transition-transform', showNeeds && 'rotate-180')} />
          </button>
          {showNeeds && <div className="mt-2"><NeedsYouList items={open} compact hideCampaign /></div>}
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <LykkeConversation scope={campaignId} variant="dock" placeholder="Ask about this campaign" autoFocus emptyHint="Ask about this campaign: creators, drafts, dates, results." />
      </div>
    </div>
  );
}

export function LykkeDock({ campaignId, onHome }: { campaignId?: string | null; onHome: boolean }) {
  const l = useLykke();
  const [params, setParams] = useSearchParams();
  const needs = useNeedsYou(!!l?.available && !!campaignId, campaignId);
  const ask = params.get('lykke') === '1';

  const available = !!l?.available;
  const openDock = l?.openDock;
  useEffect(() => {
    if (!available || !ask || !openDock) return;
    openDock(campaignId || HOME);
    setParams((p) => { const n = new URLSearchParams(p); n.delete('lykke'); return n; }, { replace: true });
  }, [ask, campaignId, available, openDock, setParams]);

  if (!l?.available || onHome) return null;
  const scope = l.dock.open ? l.dock.scope : campaignId || HOME;
  const inCampaign = scope !== HOME;
  const count = actionable(needs.data).length;
  const title = inCampaign ? l.title(scope) || 'This campaign' : 'Your conversation, all campaigns';

  return (
    <>
      {!l.dock.open && (
        <button type="button" onClick={() => l.openDock(campaignId || HOME)} data-lykke-dock-button
          className="fixed bottom-5 right-5 z-[55] flex h-12 items-center gap-2 rounded-full border border-[#eaecf0] bg-white pl-1.5 pr-4 text-[13.5px] font-semibold text-[#101828] shadow-[0_8px_24px_rgba(16,24,40,0.12)] hover:shadow-[0_10px_28px_rgba(16,24,40,0.16)]">
          <LykkeAvatar size={36} live={l.voice !== 'off'} />
          {campaignId ? 'Ask Lykke about this campaign' : 'Ask Lykke'}
          {campaignId && count > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[#be43ff] px-1.5 text-[11px] font-bold text-white">{count}</span>}
        </button>
      )}
      {l.dock.open && (
        <section aria-label="Lykke" data-lykke-dock
          className="fixed bottom-5 right-5 z-[60] flex h-[min(680px,calc(100vh-6rem))] w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[#eaecf0] bg-white shadow-[0_16px_48px_rgba(16,24,40,0.18)]">
          <header className="flex items-center gap-2 border-b border-[#f2f4f7] px-3 py-2.5">
            {!inCampaign && campaignId ? (
              <button type="button" onClick={() => l.openDock(campaignId)} aria-label="Back to this campaign" className="grid h-7 w-7 place-items-center rounded-full text-[#667085] hover:bg-[#f2f4f7]"><ArrowLeft className="h-4 w-4" /></button>
            ) : null}
            <LykkeAvatar size={28} live={l.voice !== 'off'} />
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="text-[13.5px] font-semibold text-[#101828]">Lykke</span>
              <span className="truncate text-[12px] text-[#667085]">{title}</span>
            </div>
            {inCampaign && (
              <button type="button" onClick={() => l.openDock(HOME)} className="rounded-full px-2 py-1 text-[12px] font-semibold text-[#7a1fbf] hover:bg-[#be43ff]/8">All campaigns</button>
            )}
            <button type="button" onClick={() => l.closeDock()} aria-label="Close" className="grid h-7 w-7 place-items-center rounded-full text-[#667085] hover:bg-[#f2f4f7]"><X className="h-4 w-4" /></button>
          </header>
          {inCampaign ? (
            <CampaignLykkePanel key={scope} campaignId={scope} className="flex-1" />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <LykkeConversation scope={HOME} variant="dock" placeholder="Ask Lykke anything" autoFocus emptyHint="Ask about your campaigns, content or brand. Lykke can also take you to any page." />
            </div>
          )}
        </section>
      )}
    </>
  );
}
